import Link from "next/link";
import Image from "next/image";
import { Facebook, Instagram, Linkedin, Mail, Calendar } from "lucide-react";
import { PageContainer } from "@/components/ui/PageContainer";
import { IconLink } from "@/components/ui/IconLink";
import { SERVICES } from "@/lib/services";
import { BRAND_LOGO_HEIGHT, BRAND_LOGO_SRC, BRAND_LOGO_WIDTH } from "@/lib/brand";

const COMPANY_LINKS = [
  { href: "/about", label: "About" },
  { href: "/collab", label: "Collab model" },
  { href: "/portfolio", label: "Work" },
  { href: "/deck", label: "Company deck" },
  { href: "/book", label: "Schedule a call" },
  { href: "/contact", label: "Contact" },
  { href: "/privacy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
];

const INSIGHTS_LINKS = [
  { href: "/solutions", label: "Solutions" },
  { href: "/blog", label: "Blog" },
  { href: "/guides", label: "Guides" },
  { href: "/resources", label: "Resources" },
];

const MARKET_LINKS = [
  { href: "/us", label: "United States" },
  { href: "/uk", label: "United Kingdom" },
  { href: "/au", label: "Australia" },
  { href: "/uae", label: "United Arab Emirates" },
];

const PRODUCT_LINKS = [
  { href: "/blueprint", label: "Blueprint" },
  { href: "https://studio.doimpo.com", label: "Studio", external: true },
];

export default function Footer() {
  return (
    <footer className="section-glow-top relative border-t border-white/5">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent"
        aria-hidden
      />
      <PageContainer className="py-14 md:py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          <div>
            <Link href="/" className="inline-flex items-center">
              <Image
                src={BRAND_LOGO_SRC}
                alt="doimpo"
                width={BRAND_LOGO_WIDTH}
                height={BRAND_LOGO_HEIGHT}
                className="h-8 w-auto"
                unoptimized
              />
            </Link>
            <p className="font-ui mt-4 max-w-xs text-sm leading-relaxed text-zinc-400">
              Strategy, brand, product, and marketing under one roof—for teams in the US, UK, AU, and UAE.
            </p>
          </div>

          <div>
            <p className="font-ui mb-4 text-sm font-semibold text-white">Services</p>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 5).map((s) => (
                <li key={s.slug}>
                  <Link
                    href={s.href}
                    className="font-ui text-sm text-zinc-400 transition-colors hover:text-accent"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href="/services"
                  className="font-ui text-sm font-semibold text-accent hover:text-accent-hover"
                >
                  <IconLink>All services</IconLink>
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-ui mb-4 text-sm font-semibold text-white">Company</p>
            <ul className="space-y-2.5">
              {COMPANY_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-ui text-sm text-zinc-400 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-ui mb-4 text-sm font-semibold text-white">Explore</p>
            <ul className="space-y-2.5">
              {INSIGHTS_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-ui text-sm text-zinc-400 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              {PRODUCT_LINKS.map((link) => (
                <li key={link.href}>
                  {"external" in link && link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-ui text-sm text-zinc-400 transition-colors hover:text-white"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="font-ui text-sm text-zinc-400 transition-colors hover:text-white"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <p className="font-ui mb-3 mt-6 text-sm font-semibold text-white">Markets</p>
            <ul className="space-y-2.5">
              {MARKET_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-ui text-sm text-zinc-400 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-2 text-sm text-zinc-400">
              <a
                href="mailto:hello@doimpo.com"
                className="font-ui flex items-center gap-2 transition-colors hover:text-accent"
              >
                <Mail className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
                hello@doimpo.com
              </a>
              <Link
                href="/book"
                className="font-ui flex items-center gap-2 transition-colors hover:text-accent"
              >
                <Calendar className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
                Book time with us
              </Link>
            </div>
            <p className="font-ui mt-4 text-xs leading-relaxed text-zinc-400">
              Registered office: Tower 6, 2710, My Home Tridasa, Thellapur, Hyderabad 502032, India
            </p>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-center sm:flex-row sm:text-left">
          <div className="space-y-1">
            <p className="font-ui text-sm text-zinc-400">
              © {new Date().getFullYear()} doimpo. All rights reserved.
            </p>
            <p className="font-ui text-xs text-zinc-400">
              Doimpo Collab LLP · Established July 2024
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://www.linkedin.com/company/doimpo"
              target="_blank"
              rel="noopener noreferrer"
              className="font-ui flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-accent"
              aria-label="LinkedIn"
            >
              <Linkedin className="size-4" strokeWidth={1.75} aria-hidden />
              LinkedIn
            </a>
            <a
              href="https://www.instagram.com/doimpo_collab"
              target="_blank"
              rel="noopener noreferrer"
              className="font-ui flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-accent"
              aria-label="Instagram"
            >
              <Instagram className="size-4" strokeWidth={1.75} aria-hidden />
              Instagram
            </a>
            <a
              href="https://www.facebook.com/doimpo"
              target="_blank"
              rel="noopener noreferrer"
              className="font-ui flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-accent"
              aria-label="Facebook"
            >
              <Facebook className="size-4" strokeWidth={1.75} aria-hidden />
              Facebook
            </a>
          </div>
        </div>
      </PageContainer>
    </footer>
  );
}
